import { useTranslations } from "next-intl";
import { Tooltip, TooltipContent, TooltipTrigger } from "../shadcn/tooltip";
import { NoxusAlert } from "../UI/NoxusAlert";
import { CSATFeedbackWidget } from "./CSATFeedbackWidget";
import type { NoxusDataTypes, NoxusMessagePart } from "./noxus-message-types";
import { PresentedFileWidget } from "./PresentedFileWidget";

/**
 * Props for a data part renderer component.
 * Each renderer receives the typed data payload for its data part key.
 */
export type DataPartRendererProps<K extends keyof NoxusDataTypes> = {
  data: NoxusDataTypes[K];
  id?: string;
  /** Set when the chat widget is rendering a public embed — renderers that
   *  resolve spot:// URLs should pass this to ``getSpotFileUrl``. */
  isPublic?: boolean;
  /** Conversation context for renderers that call the backend. */
  groupId?: string;
  conversationId?: string;
};

type DataPartRenderer<K extends keyof NoxusDataTypes> = React.FC<
  DataPartRendererProps<K>
>;

/**
 * Registry mapping data part keys to their renderer components.
 *
 * Tool-associated parts (run_workflow, call_agent, subagent_task) are consumed
 * by tool renderers via findDataPart(), not rendered here.
 *
 * data-error is handled inline in NoxusMessageContent (needs message context).
 */
const CompactionDivider: DataPartRenderer<"compaction"> = ({ data }) => {
  const t = useTranslations("widget.compaction");
  const label = data.status === "completed" ? t("summarized") : t("failed");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-2 my-4 text-muted-foreground text-xs cursor-help">
          <div className="flex-1 border-t border-dashed border-muted-foreground/40" />
          <span className="shrink-0">{label}</span>
          <div className="flex-1 border-t border-dashed border-muted-foreground/40" />
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" align="center" className="max-w-sm">
        <div className="flex flex-col gap-1 py-0.5 text-xs">
          <div className="font-medium text-[11px] uppercase tracking-wide opacity-70">
            {t("whatHappened")}
          </div>
          <p>{t("explanation")}</p>
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const dataPartRenderers: {
  [K in keyof NoxusDataTypes]?: DataPartRenderer<K>;
} = {
  compaction: CompactionDivider,
  warning: ({ data }) => (
    <NoxusAlert
      variant="warning"
      title={data.title ?? undefined}
      description={data.message}
      className="my-2"
    />
  ),
  file_attachment: ({ data, isPublic }) => (
    <PresentedFileWidget data={data} isPublic={isPublic} />
  ),
  csat_request: ({ groupId, conversationId }) =>
    groupId && conversationId ? (
      <CSATFeedbackWidget groupId={groupId} conversationId={conversationId} />
    ) : null,
};

/* Part types that are tool-associated metadata (not standalone renderable). */
export const TOOL_METADATA_DATA_PARTS: ReadonlySet<string> =
  new Set<`data-${keyof NoxusDataTypes}`>([
    "data-run_workflow",
    "data-call_agent",
    "data-code_mode_calls",
  ]);

/**
 * Render a data part using the registry.
 * Returns null for tool-associated metadata parts, unregistered parts,
 * and parts handled inline (like data-error).
 */
export function renderDataPart(
  part: NoxusMessagePart,
  reactKey?: string,
  isPublic?: boolean,
  context?: { groupId?: string; conversationId?: string }
): React.ReactElement | null {
  if (!part.type.startsWith("data-") || !("data" in part)) return null;

  if (TOOL_METADATA_DATA_PARTS.has(part.type)) return null;

  const dataKey = part.type.slice(5) as keyof NoxusDataTypes;

  const Renderer = dataPartRenderers[dataKey] as
    | DataPartRenderer<typeof dataKey>
    | undefined;
  if (!Renderer) return null;

  const id = "id" in part ? (part.id as string | undefined) : undefined;
  return (
    <Renderer
      key={reactKey}
      data={part.data as NoxusDataTypes[typeof dataKey]}
      id={id}
      isPublic={isPublic}
      groupId={context?.groupId}
      conversationId={context?.conversationId}
    />
  );
}
